import { Component, computed, effect, inject, model, OnInit, output, signal, viewChild } from '@angular/core';
import _, { sortBy } from 'lodash';
import { BehaviorSubject, forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { PanModalComponent } from '../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../error.utils';
import { BranchService } from '../../service/branch.service';
import { NotifyService } from '../../service/notify.service';
import { PanoptycAdminService } from '../../service/panoptyc-admin.service';
import { PosthogService } from '../../service/posthog.service';
import { UserLoginService } from '../../service/user-login.service';
import { ArchivedLocation, Branch, BranchSetting, Customer, FeatureFlag, LocationResponse } from '../../types';
import { BusinessVerticalService } from '../../service/business-vertical.service';
import { BusinessVertical } from '../../types/business-vertical';

@Component({
  selector: 'app-branch-main-list-view',
  templateUrl: './branch-main-list-view.component.html',
  styleUrls: ['./branch-main-list-view.component.scss'],
  standalone: false,
})
export class BranchMainListViewComponent implements OnInit {
  private adminService = inject(PanoptycAdminService);
  private notifyService = inject(NotifyService);
  private userService = inject(UserLoginService);
  private branchService = inject(BranchService);
  private verticalService = inject(BusinessVerticalService);
  private posthogService = inject(PosthogService);

  public branch = model.required<Branch>();
  public showArchived = signal<boolean>(false);
  public archivedLocations = signal<ArchivedLocation[]>([]);
  public isLoadingBranchLocations = signal<boolean>(false);
  public isSavingBusinessVertical = signal<boolean>(false);
  public customerChoices = signal<Customer[]>([]);
  public isLoadingCustomers = signal<boolean>(false);
  public isPendingCustomer = signal<boolean>(false);
  public customersError = signal<string | undefined>(undefined);
  public customerId = signal<string>('');
  public customerSubject = new BehaviorSubject<string | undefined>(this.customerId());

  public whitelistDomains = signal<string[]>([]);
  public whitelistConfirmed = signal<boolean>(false);
  public branchWhitelistModal = viewChild<PanModalComponent>('branchWhitelistModal');

  public sortedCustomerChoices = computed(() => sortBy(this.customerChoices(), (c) => c.name.trim().toLowerCase()));
  // with the netsuite migration on, customer tags only come from the hubspot company sync
  public canManageCustomerTags = computed(
    () => !(this.posthogService.isReady() && this.posthogService.isFeatureEnabledByEnum(FeatureFlag.EnableNetsuiteMigration)),
  );

  public validBusinessVerticals = signal<BusinessVertical[]>([]);
  public selectedLocation = signal<LocationResponse | null>(null);
  public isLocationFlagLoading = signal<boolean>(false);

  public locations = signal<LocationResponse[]>([]);
  public branchCustomer = computed<Customer | null>(() =>
    this.branch().customer_id ? this.customerChoices().find((c) => c.id === this.branch().customer_id) : null,
  );

  public branchUpdate = output<{ id: number; updatedBranch?: Branch }>();
  public removeCustomer = output();

  constructor() {
    effect(() => {
      const customerId = this.branch().customer_id ?? undefined;
      this.customerId.set(customerId);
      this.customerSubject.next(customerId);
    });
  }

  ngOnInit() {
    this.loadCustomersAndVerticals();
    this.customerId.set(this.branch().customer_id);
    this.whitelistDomains.set(this.branch()?.keystore.allowed_domains ?? []);
  }

  loadCustomersAndVerticals() {
    if (!this.userService.currentUserIsAdmin()) return;

    this.isLoadingCustomers.set(true);
    this.customersError.set(undefined);
    forkJoin({
      customers: this.adminService.getCustomers(),
      verticalsResponse: this.verticalService.getAllBusinessVerticals(),
    }).subscribe({
      next: ({ customers, verticalsResponse }) => {
        this.customerChoices.set(customers);
        this.validBusinessVerticals.set(verticalsResponse.items);
        this.isLoadingCustomers.set(false);
      },
      error: (err) => {
        this.customersError.set(tryGetErrorMessage(err));
        this.isLoadingCustomers.set(false);
      },
    });
  }

  async onCustomerChange(customer: Partial<Customer> | undefined) {
    const branchId = this.branch().id;
    const updateBranch = (customerId: string) =>
      this.adminService.updateBranchCustomer(branchId, customerId).subscribe(
        (updatedBranch) => {
          this.notifyService.notify('Successfully updated customer');
          this.isPendingCustomer.set(false);
          // we are preparing a manually updated BranchImpl
          const orig = this.branch();
          this.branchUpdate.emit({ id: branchId, updatedBranch: { ...orig, customer_id: updatedBranch.customer_id } });
        },
        (err) => {
          this.customersError.set(tryGetErrorMessage(err));
          this.isPendingCustomer.set(false);
        },
      );

    if (!customer) {
      const result = await Swal.fire({
        title: 'Clear Customer Tag',
        text: `Are you sure you want to clear the customer tag "${this.branchCustomer()?.name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Clear',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) {
        // reset the input value (because it was just cleared)
        this.customerId.set(this.branchCustomer().id);
        return;
      }
      updateBranch(null);
      return;
    }

    this.isPendingCustomer.set(true);
    this.customersError.set(undefined);

    // Handle new customer creation
    if (customer.id === undefined && !_.isEmpty(customer.name)) {
      const result = await Swal.fire({
        title: 'Create Customer Tag',
        text: `Are you sure you want to create a new customer tag "${customer.name}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Create',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;
      this.adminService.createCustomer(customer.name).subscribe(
        (newCustomer) => {
          this.notifyService.notify('Successfully created new customer tag');
          this.loadCustomersAndVerticals();
          updateBranch(newCustomer.id);
        },
        (err) => {
          this.customersError.set(tryGetErrorMessage(err));
          this.isPendingCustomer.set(false);
        },
      );
    }
    // Handle existing customer update
    else if (this.branch().customer_id !== customer.id) {
      updateBranch(customer.id!);
    }
  }

  removeCustomerById(customerId: string) {
    const customer = this.customerChoices().find((c) => c.id === customerId);
    const branchId = this.branch().id;
    this.adminService.removeCustomer(customerId).subscribe(
      () => {
        this.notifyService.notify(`Successfully removed customer tag "${customer?.name}"`);
        this.loadCustomersAndVerticals();
        // update this branch immediately
        this.branchUpdate.emit({ id: branchId });
        // the customer tag may have been tagged into other branches; refresh the dataset
        this.removeCustomer.emit();
      },
      (err) => {
        this.customersError.set(tryGetErrorMessage(err));
        this.isPendingCustomer.set(false);
      },
    );
  }

  changeBusinessVertical(vertical: BusinessVertical) {
    this.isSavingBusinessVertical.set(true);
    this.branchService.updateBranchBusinessVertical(this.branch().id, { business_vertical_id: vertical.id }).subscribe({
      next: () => {
        this.notifyService.notify('Successfully updated business vertical');
        this.isSavingBusinessVertical.set(false);
      },
      error: (err) => {
        this.notifyService.notify(`Failed to update business vertical: ${tryGetErrorMessage(err)}`);
        this.isSavingBusinessVertical.set(false);
      },
      complete: () => {
        this.isSavingBusinessVertical.set(false);
      },
    });
  }

  updateFacialRecognition() {
    const newValue = !this.branch().face_recognition_enabled;
    this.adminService.updateFaceRecognition(this.branch().id, newValue).subscribe(() => {
      this.branch.update((b) => ({
        ...b,
        face_recognition_enabled: newValue,
      }));
    });
  }

  setBranchSettingEnabled(enabled: boolean, setting: BranchSetting) {
    this.branchService.updateBranchSetting(this.branch().id, setting, enabled).subscribe(() => {
      this.branch.update((b) => {
        const newBranchValues: Branch = {
          ...b,
          keystore: b.keystore ?? {},
        };
        newBranchValues.keystore[setting] = enabled;
        return newBranchValues;
      });
    });
  }

  updateEmailOptOut(enabled: boolean) {
    this.adminService.updateEmailOptOut(this.branch().id, enabled).subscribe(() => {
      this.branch.update((b) => {
        const newBranchValues: Branch = {
          ...b,
          keystore: b.keystore ?? {},
        };
        newBranchValues.keystore.theft_report_options = {
          ...newBranchValues.keystore.theft_report_options,
          skip_email: enabled,
        };
        return newBranchValues;
      });
    });
  }

  getCustomerTypeName(branch: Branch) {
    return branch.customer_type.replace('_', ' ');
  }

  getRemoveCustomerTagText(branchCustomer: Customer) {
    return (
      'Are you sure you want to remove this customer tag "' + branchCustomer?.name + '"? Any other branches will lose this customer tag.'
    );
  }

  public confirmBranchWhitelistDetails() {
    this.whitelistConfirmed.set(true);

    this.branchService.updateBranchAllowedDomains(this.branch()?.id, { allowed_domains: this.whitelistDomains() }).subscribe({
      next: () => {
        this.notifyService.notify('Successfully updated email domain whitelist');
        this.branch.update((b) => ({
          ...b,
          keystore: {
            ...b.keystore,
            allowed_domains: this.whitelistDomains(),
          },
        }));
      },
      error: (err) => {
        this.notifyService.notify('Failed to update email domain whitelist: ' + tryGetErrorMessage(err));
        this.whitelistDomains.set(this.branch()?.keystore.allowed_domains ?? []);
      },
      complete: () => {
        this.branchWhitelistModal().hide();
      },
    });
  }

  public addTagFn(newTag: string) {
    return newTag;
  }

  public onBranchWhitelistModalOpen() {
    this.whitelistConfirmed.set(false);
  }

  public onBranchWhitelistModalClose() {
    if (!this.whitelistConfirmed()) {
      this.whitelistDomains.set(this.branch()?.keystore.allowed_domains ?? []);
    }
  }
}
